import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { authAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Settings as SettingsIcon, 
  Lock,
  User,
  ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function Settings() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setLoading(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      toast.success('Passwort erfolgreich geändert');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Failed to change password:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-8" data-testid="settings-page">
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to={user?.role === 'admin' ? '/admin' : '/dashboard'}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
          </Link>
          <h1 className="font-barlow font-bold text-2xl text-[#1A1A1A]">
            Einstellungen
          </h1>
        </div>

        {/* Profile Info */}
        <Card className="mb-6" data-testid="profile-card">
          <CardHeader>
            <CardTitle className="font-barlow text-xl flex items-center gap-2">
              <User className="w-5 h-5 text-[#9B1B30]" />
              Profil
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="font-manrope text-sm text-gray-500">Name</p>
                <p className="font-manrope font-medium">{user?.first_name} {user?.last_name}</p>
              </div>
              <div>
                <p className="font-manrope text-sm text-gray-500">E-Mail</p>
                <p className="font-manrope font-medium">{user?.email}</p>
              </div>
              <div>
                <p className="font-manrope text-sm text-gray-500">Telefon</p>
                <p className="font-manrope font-medium">{user?.phone}</p>
              </div>
              {user?.customer_number && (
                <div>
                  <p className="font-manrope text-sm text-gray-500">Kundennummer</p>
                  <p className="font-barlow font-bold text-[#9B1B30]">{user?.customer_number}</p>
                </div>
              )}
              <div className="md:col-span-2">
                <p className="font-manrope text-sm text-gray-500">Adresse</p>
                <p className="font-manrope font-medium">{user?.street}</p>
                <p className="font-manrope font-medium">{user?.postal_code} {user?.city}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card data-testid="password-card">
          <CardHeader>
            <CardTitle className="font-barlow text-xl flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#2E2A88]" />
              Passwort ändern
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label className="font-barlow font-bold text-sm">Aktuelles Passwort</Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-1"
                  required
                  data-testid="current-password"
                />
              </div>
              <div>
                <Label className="font-barlow font-bold text-sm">Neues Passwort</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1"
                  placeholder="Min. 8 Zeichen"
                  required
                  data-testid="new-password"
                />
              </div>
              <div>
                <Label className="font-barlow font-bold text-sm">Neues Passwort bestätigen</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1"
                  required
                  data-testid="confirm-password"
                />
              </div>
              <Button 
                type="submit" 
                className="btn-primary"
                disabled={loading}
                data-testid="save-password-btn"
              >
                {loading ? 'Wird gespeichert...' : 'Passwort ändern'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
